import React from 'react';
import { ListRenderItem } from 'react-native';
import { Modal as ModalBase } from 'react-native-paper';
import { Trans } from '@lingui/macro';
import styled from 'styled-components/native';

import RefreshControl from '@src/components/RefreshControl';
import { CollectionPreviewStatus as Status } from '@src/enums';
import { CollectionPreview } from '@src/types';

import { StatusPicker } from './StatusPicker';
import {
  Bullet,
  CollectionItem,
  CollectionLabel,
  Collections,
  SubmitButton,
  Title,
} from './styled';
import useGetCollections from './useGetCollections';

type Props = {
  visible: boolean;
  onDismiss?: () => void;
  onCollectionSelect?: (collectionId: string) => void;
  onSubmit?: () => void;
  activeCollectionId?: string;
};

const Modal = styled(ModalBase)``;

const Container = styled.View`
  width: 90%;
  height: 90%;
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.roundness}px;
  justify-content: center;
  align-items: center;
  align-self: center;
  padding: 20px 5px;
`;

const Component: React.FC<Props> = (props) => {
  const {
    visible,
    onDismiss,
    onCollectionSelect,
    onSubmit,
    activeCollectionId,
  } = props;

  const [status, setStatus] = React.useState(Status.PUBLISHED);
  const {
    data,
    handleEndReached,
    handleRefresh,
    loading,
    refreshing,
  } = useGetCollections(status);

  const renderItem: ListRenderItem<CollectionPreview> = React.useCallback(
    ({ item }) => {
      return (
        <CollectionItem
          active={activeCollectionId === item.id}
          onPress={() => onCollectionSelect?.(item.id)}
        >
          <Bullet active={activeCollectionId === item.id} />
          <CollectionLabel active={activeCollectionId === item.id}>
            {item.title}
          </CollectionLabel>
        </CollectionItem>
      );
    },
    [activeCollectionId, onCollectionSelect]
  );

  return (
    <Modal visible={visible} onDismiss={onDismiss}>
      <Container>
        <Title>
          <Trans>Add to Collection</Trans>
        </Title>
        <StatusPicker status={status} onChangeStatus={setStatus} />
        <Collections
          data={data}
          renderItem={renderItem}
          onEndReached={handleEndReached}
          refreshControl={
            <RefreshControl
              refreshing={refreshing || loading}
              onRefresh={handleRefresh}
            />
          }
          ListEmptyComponent={
            <CollectionLabel active={false}>
              <Trans>No Collections</Trans>
            </CollectionLabel>
          }
        />
        <SubmitButton disabled={!activeCollectionId} onPress={onSubmit}>
          <Trans>Submit</Trans>
        </SubmitButton>
      </Container>
    </Modal>
  );
};

export default React.memo(Component);
